import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Switch,
  Typography,
  styled,
} from "@mui/material";
import { useConsent } from "../../hooks/useConsent";
import { CONSENT_TEXTS } from "../../pages/shared/constants";
import { formatDateSpanish } from "../../utils/format";

/**
 * Where a decision gets changed (EB-559).
 *
 * Consent that cannot be withdrawn as easily as it was given is not consent, so this is reachable
 * from the footer of every page — the same reach the banner had when it asked.
 *
 * The necessary category is shown with its switch **on and disabled** rather than hidden. A shopper
 * who is told only about the thing they can turn off has no way to find out what else is stored,
 * and "necessary" is a claim worth making visible enough to be challenged.
 */

const CategoryBody = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginLeft: theme.spacing(6),
  marginTop: theme.spacing(-0.5),
}));

type Props = {
  open: boolean;
  onClose: () => void;
};

const CookiePreferencesDialog: React.FC<Props> = ({ open, onClose }) => {
  const { decision, decidedAt, decide } = useConsent();

  // Local until saved, so toggling the switch and closing without saving changes nothing — the
  // dialog is a form, not a set of live controls.
  const [analytics, setAnalytics] = useState(decision === "granted");

  // Re-seeded on each open rather than once on mount: the dialog stays mounted between openings,
  // and a stale toggle would offer to "save" a decision the shopper never made this time.
  useEffect(() => {
    if (open) setAnalytics(decision === "granted");
  }, [open, decision]);

  const save = () => {
    decide(analytics ? "granted" : "denied");
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{CONSENT_TEXTS.prefsTitle}</DialogTitle>

      <DialogContent dividers>
        <Box mb={2}>
          <FormControlLabel
            control={<Switch checked disabled />}
            label={
              <Typography sx={{ fontWeight: 700 }}>{CONSENT_TEXTS.prefsNecessaryTitle}</Typography>
            }
          />
          <CategoryBody variant="body2">{CONSENT_TEXTS.prefsNecessaryBody}</CategoryBody>
        </Box>

        <Divider />

        <Box mt={2}>
          <FormControlLabel
            control={
              <Switch
                checked={analytics}
                onChange={(event) => setAnalytics(event.target.checked)}
                inputProps={{ "aria-label": CONSENT_TEXTS.prefsAnalyticsLabel }}
              />
            }
            label={
              <Typography sx={{ fontWeight: 700 }}>{CONSENT_TEXTS.prefsAnalyticsTitle}</Typography>
            }
          />
          <CategoryBody variant="body2">{CONSENT_TEXTS.prefsAnalyticsBody}</CategoryBody>
        </Box>

        <Typography variant="caption" color="text.secondary" display="block" mt={3}>
          {decidedAt
            ? CONSENT_TEXTS.prefsDecidedOn(formatDateSpanish(decidedAt))
            : CONSENT_TEXTS.prefsNeverDecided}
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          {CONSENT_TEXTS.prefsClose}
        </Button>
        <Button onClick={save} variant="contained" sx={{ textTransform: "none", fontWeight: 700 }}>
          {CONSENT_TEXTS.prefsSave}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CookiePreferencesDialog;
